// Package 4, part D (owner, 7. 10. 2026; the world book): named enemies, one in each zone. A rare sight on a run there: a
// chance at each fight that grows with every fight there without it (and resets when it comes); far tougher than the
// zone's own; beaten, a sure Rare or better find and a page in the Collection book ("Named"). Each week one of them is
// the bounty: beaten that week, 5 Relic Shards and Season Score points (the owner's to confirm: 60, a top horde's).

import type { ZoneId } from './types';

export type NamedId = 'hermit' | 'garrity' | 'salesman' | 'patient07' | 'dahl' | 'unit9' | 'the_first' | 'ashwalker';

export interface NamedDef {
  id: NamedId;
  name: string;
  zone: ZoneId;
  /** What it fights as (its stats are this kind's, made tougher). */
  type: 'infected' | 'raider' | 'mutant' | 'drone';
  /** One line for the report and the book. */
  line: string;
  /** Its portrait in the art set (enemies folder; 512, 256 and 128). */
  art: string;
}

export const NAMED: Record<NamedId, NamedDef> = {
  hermit: { id: 'hermit', name: 'The Hermit', zone: 'forest', type: 'infected', line: 'He kept the trail clear for twenty years. He still does.', art: 'enemy_infected_hermit' },
  garrity: { id: 'garrity', name: 'Old Man Garrity', zone: 'gas_station', type: 'raider', line: 'The Tollmen collect the toll. Garrity collects it in person.', art: 'enemy_raider_garrity' },
  salesman: { id: 'salesman', name: 'The Salesman', zone: 'downtown', type: 'infected', line: 'Still in the suit. Still smiling. Still selling MEND.', art: 'enemy_infected_salesman' },
  patient07: { id: 'patient07', name: 'Patient 07', zone: 'hospital', type: 'mutant', line: 'The wristband reads: Trial 07. Batch 1.', art: 'enemy_mutant_patient07' },
  dahl: { id: 'dahl', name: 'Forewoman Dahl', zone: 'factory', type: 'raider', line: 'She runs the Foundry line, and the guns that come off it.', art: 'enemy_raider_dahl' },
  unit9: { id: 'unit9', name: 'Unit 9', zone: 'military_base', type: 'drone', line: 'WARDEN Enforcer, unit nine. It still enforces a curfew nobody lifted.', art: 'enemy_drone_unit9' },
  the_first: { id: 'the_first', name: 'The First', zone: 'mountains', type: 'mutant', line: 'The first one to change. It remembers being a man.', art: 'enemy_mutant_the_first' },
  ashwalker: { id: 'ashwalker', name: 'Ashwalker', zone: 'wasteland', type: 'mutant', line: 'It walks through The Burn and does not burn.', art: 'enemy_mutant_ashwalker' },
};

export const NAMED_IDS = Object.keys(NAMED) as NamedId[];

export const NAMED_RULES = {
  on: true,
  /** The chance at a fight in its zone, and how much it grows with every fight there without it. */
  baseChance: 0.005,
  perFight: 0.001,
  /** The week's bounty shows up this much more often. */
  bountyMult: 2,
  /** Tougher than the zone's own: levels above the survivor (its level kept in the zone's recommended range), HP and attack. */
  levelOver: 2,
  hpMult: 3,
  attackMult: 1.2,
  /** Its find: Rare, or Epic this share of the time. */
  epicShare: 0.15,
  /** The week's bounty: Relic Shards and Season Score points. */
  bountyShards: 5,
  bountyScore: 60,
};

export const namedOfZone = (zone: ZoneId): NamedDef | null => Object.values(NAMED).find((n) => n.zone === zone) ?? null;

/**
 * Its level: two above the survivor, the survivor's level taken inside the zone's recommended range (so a survivor at the
 * bottom of the range meets a beatable one, and one above the range no pushover); at most 22.
 */
export function namedLevel(recommended: readonly [number, number], playerLevel: number): number {
  return Math.min(22, Math.min(recommended[1], Math.max(recommended[0], playerLevel)) + NAMED_RULES.levelOver);
}

/** The chance at the next fight in its zone after `since` fights there without it. */
export function namedChance(since: number, bounty: boolean): number {
  if (!NAMED_RULES.on) return 0;
  return Math.min(1, (NAMED_RULES.baseChance + NAMED_RULES.perFight * Math.max(0, since)) * (bounty ? NAMED_RULES.bountyMult : 1));
}

/** The week's bounty: one named enemy a week, in turn (by the Monday of the week, "2026-10-05"). */
export function weeklyBountyOf(weekMonday: string): NamedId {
  const days = Math.floor(Date.parse(`${weekMonday}T00:00:00Z`) / 86_400_000);
  return NAMED_IDS[Math.floor(days / 7) % NAMED_IDS.length];
}

/** The Monday of the week of a moment (server time, as the weekly contracts). */
export function mondayOf(ms: number): string {
  const d = new Date(ms);
  const day = (d.getUTCDay() + 6) % 7;
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day));
  return m.toISOString().slice(0, 10);
}
